import React, { useMemo, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AppModal from './AppModal';
import { Spin, Form, Input, InputNumber, Checkbox, message, Modal } from 'antd';
import StatusBadge from '@/components/ui/StatusBadge';
import Table from '@/components/ui/table/Table';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import {
    useProjectRequests,
    formatProjectRequestDate,
    formatProjectRequestAmount,
} from '@hooks/open-market/useProjectRequests';
import ListBodyLayout from '@/components/layout/ListBodyLayout';
import {
    CreateRoleModalBody,
    CreateRoleModalFooter,
    ModalSectionHeading,
    ModalInfoBlock,
} from './style';
import { TablePagination, StyledPagination } from '@/components/ui/table/style';
import { PROJECT_CLIENT_TYPE } from '@/constants/projectClientType';
import { budgetFromFormValue } from '@hooks/projects/projectHelpers';

const openMarketOverviewPath = (pathname) =>
    pathname?.startsWith('/tenant') ? '/tenant/open-market/overview' : '/open-market/overview';

const projectsPath = (pathname) => (pathname?.startsWith('/tenant') ? '/tenant/projects' : '/projects');

const OpenMarketProjectRequests = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [acceptModalRequestId, setAcceptModalRequestId] = useState(null);
    const [acceptForm] = Form.useForm();
    const [createAuditModalRecord, setCreateAuditModalRecord] = useState(null);
    const [createAuditForm] = Form.useForm();

    const {
        items,
        total,
        page,
        setPage,
        limit,
        isLoading,
        error,
        isClient,
        canRespond,
        respondToRequest,
        respondPending,
        createProjectWithPayload,
        createProjectPending,
        createAuditWithPayload,
        createAuditPending,
    } = useProjectRequests({ limit: 10 });

    const [createProjectModalRecord, setCreateProjectModalRecord] = useState(null);
    const [createProjectForm] = Form.useForm();

    const tableData = useMemo(
        () =>
            items.map((row) => ({
                key: row.id,
                ...row,
            })),
        [items],
    );

    const closeAcceptModal = useCallback(() => {
        setAcceptModalRequestId(null);
        acceptForm.resetFields();
    }, [acceptForm]);

    const handleAcceptSubmit = useCallback(() => {
        acceptForm.validateFields().then((values) => {
            const note = (values.note ?? '').trim();
            respondToRequest(
                acceptModalRequestId,
                { action: 'approved', note },
                {
                    onSuccess: () => {
                        message.success('Request approved');
                        closeAcceptModal();
                    },
                    onError: (err) => {
                        message.error(err?.response?.data?.message || 'Failed to approve request');
                    },
                },
            );
        });
    }, [acceptModalRequestId, acceptForm, respondToRequest, closeAcceptModal]);

    const handleRejectClick = useCallback(
        (e, record) => {
            e.stopPropagation();
            Modal.confirm({
                title: 'Reject request?',
                content: 'This project request will be rejected.',
                maskClosable: false,
                okText: 'Yes, reject',
                cancelText: 'No',
                okButtonProps: { danger: true },
                onOk: () => {
                    respondToRequest(
                        record.id,
                        { action: 'rejected', note: '' },
                        {
                            onSuccess: () => message.success('Request rejected'),
                            onError: (err) => message.error(err?.response?.data?.message || 'Failed to reject request'),
                        },
                    );
                },
            });
        },
        [respondToRequest],
    );

    const closeCreateProjectModal = useCallback(() => {
        setCreateProjectModalRecord(null);
        createProjectForm.resetFields();
    }, [createProjectForm]);

    const handleCreateProjectSubmit = useCallback(() => {
        if (!createProjectModalRecord) return;
        createProjectForm.validateFields().then((values) => {
            const clientId = values.clientId ?? createProjectModalRecord.requester?.id;
            if (!clientId) {
                message.error('Client is required');
                return;
            }
            createProjectWithPayload(
                {
                    requestId: createProjectModalRecord.id,
                    body: {
                        name: (values.name ?? '').trim() || createProjectModalRecord.openMarketLead?.title || 'Project',
                        clientId,
                        budget: budgetFromFormValue(values.budget),
                        priority: (Array.isArray(values.priority) ? values.priority[0] : values.priority) || 'high',
                        clientType: PROJECT_CLIENT_TYPE.MINE_AND_PLATFORM,
                    },
                },
                {
                    onSuccess: () => {
                        message.success('Project created');
                        closeCreateProjectModal();
                        navigate(projectsPath(location.pathname));
                    },
                    onError: (err) => {
                        message.error(err?.response?.data?.message || err?.message || 'Failed to create project');
                    },
                },
            );
        });
    }, [
        createProjectModalRecord,
        createProjectForm,
        createProjectWithPayload,
        closeCreateProjectModal,
        navigate,
        location.pathname,
    ]);

    const closeCreateAuditModal = useCallback(() => {
        setCreateAuditModalRecord(null);
        createAuditForm.resetFields();
    }, [createAuditForm]);

    const handleCreateAuditSubmit = useCallback(() => {
        if (!createAuditModalRecord) return;
        createAuditForm.validateFields().then((values) => {
            const clientId = values.clientId ?? createAuditModalRecord.requester?.id;
            if (!clientId) {
                message.error('Client is required');
                return;
            }
            createAuditWithPayload(
                {
                    requestId: createAuditModalRecord.id,
                    body: {
                        name:
                            (values.name ?? '').trim() ||
                            `${createAuditModalRecord.openMarketLead?.title || 'Project'} Audit`,
                        description: (values.description ?? '').trim(),
                        clientId,
                    },
                },
                {
                    onSuccess: () => {
                        message.success('Audit created');
                        closeCreateAuditModal();
                    },
                    onError: (err) => {
                        message.error(err?.response?.data?.message || err?.message || 'Failed to create audit');
                    },
                },
            );
        });
    }, [createAuditModalRecord, createAuditForm, createAuditWithPayload, closeCreateAuditModal]);

    const baseColumns = useMemo(
        () => [
            {
                title: 'Date',
                key: 'createdAt',
                render: (_, record) => formatProjectRequestDate(record.createdAt),
            },
            {
                title: 'Requester',
                key: 'requester',
                render: (_, record) => {
                    const r = record.requester;
                    if (!r) return '—';
                    return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <span>{r.name ?? '—'}</span>
                            {r.email && (
                                <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)' }}>{r.email}</span>
                            )}
                        </div>
                    );
                },
            },
            {
                title: 'Lead',
                key: 'lead',
                dataIndex: 'openMarketLead',
                render: (lead) => lead?.title ?? '—',
            },
            {
                title: 'Amount',
                key: 'amount',
                render: (_, record) => formatProjectRequestAmount(record.bid?.amount),
            },
            ...(isClient
                ? [
                      {
                          title: 'Status',
                          key: 'status',
                          render: (_, record) => <StatusBadge status={record.status} />,
                      },
                  ]
                : canRespond
                  ? [
                        {
                            title: 'Action',
                            key: 'action',
                            width: 260,
                            align: 'center',
                            render: (_, record) => {
                                const status = (record.status || '').toLowerCase();
                                const isPending = status === 'pending';
                                const isApproved = status === 'approved';
                                const isRejected = status === 'rejected';

                                if (isRejected) {
                                    return (
                                        <span style={{ color: 'var(--ant-color-text-tertiary)', fontSize: 13 }}>
                                            Rejected
                                        </span>
                                    );
                                }
                                if (isPending) {
                                    return (
                                        <div
                                            onClick={(e) => e.stopPropagation()}
                                            style={{ display: 'inline-flex', gap: 8 }}
                                        >
                                            <Button
                                                type="button"
                                                $primary
                                                $blackOnLight
                                                $size="12px"
                                                loading={respondPending}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setAcceptModalRequestId(record.id);
                                                }}
                                            >
                                                Accept
                                            </Button>
                                            <Button
                                                type="button"
                                                $primary={false}
                                                $size="12px"
                                                disabled={respondPending}
                                                onClick={(e) => handleRejectClick(e, record)}
                                            >
                                                Reject
                                            </Button>
                                        </div>
                                    );
                                }
                                if (isApproved) {
                                    return (
                                        <div
                                            onClick={(e) => e.stopPropagation()}
                                            style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap' }}
                                        >
                                            <Button
                                                type="button"
                                                $primary
                                                $blackOnLight
                                                $size="12px"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setCreateProjectModalRecord(record);
                                                    createProjectForm.setFieldsValue({
                                                        name: record.openMarketLead?.title ?? '',
                                                        clientId: record.requester?.id ?? undefined,
                                                        budget:
                                                            record.bid?.amount != null
                                                                ? Number(record.bid.amount)
                                                                : undefined,
                                                        priority: ['high'],
                                                    });
                                                }}
                                            >
                                                Create Project
                                            </Button>
                                            <Button
                                                type="button"
                                                $primary={false}
                                                $size="12px"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setCreateAuditModalRecord(record);
                                                    createAuditForm.setFieldsValue({
                                                        name: record.openMarketLead?.title
                                                            ? `${record.openMarketLead.title} Audit`
                                                            : '',
                                                        description: '',
                                                        clientId: record.requester?.id ?? undefined,
                                                    });
                                                }}
                                            >
                                                Create Audit
                                            </Button>
                                        </div>
                                    );
                                }
                                return '—';
                            },
                        },
                    ]
                  : []),
        ],
        [isClient, canRespond, respondPending, handleRejectClick, createProjectForm, createAuditForm],
    );

    const columns = baseColumns;

    if (error) {
        return (
            <ListBodyLayout title="Project Requests" description="Requests created from accepted Open Market bids">
                <Container $display="flex" $flexDirection="column" $gap="16px" $width="100%">
                    <div style={{ padding: '24px' }}>
                        <p style={{ textAlign: 'center', opacity: 0.6 }}>Unable to load project requests. Please try again later.</p>
                    </div>
                </Container>
            </ListBodyLayout>
        );
    }

    return (
        <ListBodyLayout title="Project Requests" description="Requests created from accepted Open Market bids">
            {isLoading ? (
                <div style={{ padding: '48px', textAlign: 'center' }}>
                    <Spin size="large" />
                </div>
            ) : (
                <>
                    <Table
                        title=""
                        columns={columns}
                        dataSource={tableData}
                        showSearch={false}
                        showFilter={false}
                        showMore={false}
                        hideToolbar
                        pagination={false}
                        $topMargin={false}
                        $radius={12}
                        onRow={(record) => ({
                            onClick: () => {
                                const leadId = record.openMarketLeadId;
                                if (leadId) {
                                    navigate(
                                        `${openMarketOverviewPath(location.pathname)}?leadId=${encodeURIComponent(leadId)}`,
                                    );
                                }
                            },
                            style: { cursor: record.openMarketLeadId ? 'pointer' : 'default' },
                        })}
                    />
                    {total > 0 && (
                        <TablePagination>
                            <StyledPagination
                                current={page}
                                pageSize={limit}
                                total={total}
                                onChange={(p) => setPage(p)}
                                showSizeChanger={false}
                                showTotal={(t, range) => `${range[0]}-${range[1]} of ${t} items`}
                            />
                        </TablePagination>
                    )}
                </>
            )}

            <AppModal
                size="md"
                title="Approve project request"
                open={!!acceptModalRequestId}
                onCancel={closeAcceptModal}
                footer={null}
            >
                <CreateRoleModalBody>
                    <Form form={acceptForm} layout="vertical" initialValues={{ note: '' }}>
                        <Form.Item name="note" label="Note">
                            <Input.TextArea rows={4} placeholder="e.g. We will create the project" />
                        </Form.Item>
                    </Form>
                </CreateRoleModalBody>
                <CreateRoleModalFooter>
                    <Button $primary={false} $size="14px" onClick={closeAcceptModal}>
                        Cancel
                    </Button>
                    <Button $primary $blackOnLight $size="14px" loading={respondPending} onClick={handleAcceptSubmit}>
                        Approve
                    </Button>
                </CreateRoleModalFooter>
            </AppModal>

            <AppModal
                size="md"
                title="Create Project"
                open={!!createProjectModalRecord}
                onCancel={closeCreateProjectModal}
                footer={null}
            >
                <CreateRoleModalBody>
                    {createProjectModalRecord?.requester && (
                        <>
                            <ModalSectionHeading>Client info</ModalSectionHeading>
                            <ModalInfoBlock>
                                <div style={{ fontWeight: 500 }}>{createProjectModalRecord.requester.name || '—'}</div>
                                {createProjectModalRecord.requester.email && (
                                    <div style={{ marginTop: 4, color: 'inherit', opacity: 0.85 }}>
                                        {createProjectModalRecord.requester.email}
                                    </div>
                                )}
                            </ModalInfoBlock>
                        </>
                    )}
                    <ModalSectionHeading>Project info</ModalSectionHeading>
                    <Form form={createProjectForm} layout="vertical">
                        <Form.Item name="clientId" hidden>
                            <Input type="hidden" />
                        </Form.Item>
                        <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Enter project name' }]}>
                            <Input placeholder="e.g. Website Redesign" size="large" />
                        </Form.Item>
                        <Form.Item
                            name="priority"
                            label="Priority"
                            rules={[{ required: true, message: 'Select a priority' }]}
                        >
                            <Checkbox.Group
                                options={[
                                    { value: 'high', label: 'High' },
                                    { value: 'medium', label: 'Medium' },
                                    { value: 'low', label: 'Low' },
                                ]}
                                style={{ display: 'flex', flexDirection: 'row', gap: 16 }}
                                onChange={(checked) => {
                                    const last = checked[checked.length - 1];
                                    createProjectForm.setFieldValue('priority', last !== undefined ? [last] : []);
                                }}
                            />
                        </Form.Item>
                        <Form.Item name="budget" label="Budget">
                            <InputNumber
                                min={0}
                                placeholder="e.g. 50000"
                                style={{ width: '100%' }}
                                size="large"
                            />
                        </Form.Item>
                    </Form>
                </CreateRoleModalBody>
                <CreateRoleModalFooter>
                    <Button $primary={false} $size="14px" onClick={closeCreateProjectModal}>
                        Cancel
                    </Button>
                    <Button
                        $primary
                        $blackOnLight
                        $size="14px"
                        loading={createProjectPending}
                        onClick={handleCreateProjectSubmit}
                    >
                        Create Project
                    </Button>
                </CreateRoleModalFooter>
            </AppModal>

            <AppModal
                size="md"
                title="Create Audit"
                open={!!createAuditModalRecord}
                onCancel={closeCreateAuditModal}
                footer={null}
            >
                <CreateRoleModalBody>
                    {createAuditModalRecord?.requester && (
                        <>
                            <ModalSectionHeading>Client info</ModalSectionHeading>
                            <ModalInfoBlock>
                                <div style={{ fontWeight: 500 }}>{createAuditModalRecord.requester.name || '—'}</div>
                                {createAuditModalRecord.requester.email && (
                                    <div style={{ marginTop: 4, color: 'inherit', opacity: 0.85 }}>
                                        {createAuditModalRecord.requester.email}
                                    </div>
                                )}
                            </ModalInfoBlock>
                        </>
                    )}
                    <ModalSectionHeading>Audit info</ModalSectionHeading>
                    <Form form={createAuditForm} layout="vertical">
                        <Form.Item name="clientId" hidden>
                            <Input type="hidden" />
                        </Form.Item>
                        <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Enter audit name' }]}>
                            <Input placeholder="e.g. Q1 Code Audit" size="large" />
                        </Form.Item>
                        <Form.Item name="description" label="Description">
                            <Input.TextArea rows={4} placeholder="e.g. Annual audit" />
                        </Form.Item>
                    </Form>
                </CreateRoleModalBody>
                <CreateRoleModalFooter>
                    <Button $primary={false} $size="14px" onClick={closeCreateAuditModal}>
                        Cancel
                    </Button>
                    <Button
                        $primary
                        $blackOnLight
                        $size="14px"
                        loading={createAuditPending}
                        onClick={handleCreateAuditSubmit}
                    >
                        Create Audit
                    </Button>
                </CreateRoleModalFooter>
            </AppModal>
        </ListBodyLayout>
    );
};

export default OpenMarketProjectRequests;
